import { Routes } from '@angular/router';
import { guestGuard } from '@core/authentication/guest.guard';
import { MAINTENANCE_MODE_PATH } from '@core/maintenance/maintenance-mode.constants';
import { maintenanceModeGuard } from '@core/maintenance/maintenance-mode.guard';
import { accountModuleGuard } from '@core/tenant/tenant-account-module.guard';
import { bookingModuleGuard } from '@core/tenant/tenant-booking-module.guard';
import { subscriptionModuleGuard } from '@core/tenant/tenant-subscription-module.guard';
import { DEV_ROUTES } from '@environments/dev-tools';
import { notFoundRoute } from '@shared/components/not-found/not-found.route';

// Pages that only work in the browser for one visitor (session, login, a token in the URL) are
// kept out of search results with `robots: 'noindex'`, read by AbookTitleStrategy (#906). /search
// is blocked in robots.txt instead, so this data is not needed there.

const appRoutes: Routes = [
  {
    path: 'pending-approval',
    canActivate: [accountModuleGuard],
    data: { robots: 'noindex' },
    loadComponent: () =>
      import(
        '@features/account/pages/page-pending-approval/page-pending-approval.component'
      ).then((m) => m.PagePendingApprovalComponent),
    title: 'Konto afventer godkendelse',
  },
  {
    path: 'account/create',
    canActivate: [accountModuleGuard, guestGuard],
    data: { robots: 'noindex' },
    loadComponent: () =>
      import('@features/account/pages/page-account-create/page-account-create.component').then(
        (m) => m.PageAccountCreateComponent,
      ),
    title: 'Opret konto',
  },
  {
    // A checkout page that lives outside `path: 'checkout'`, so it needs the guard of its own:
    // it creates a real payment and hands the visitor to Pay4It. An emailed payment link is
    // the one booking entry point that does not start inside the app (#586).
    path: 'payment-redirect',
    canActivate: [bookingModuleGuard],
    data: { robots: 'noindex' },
    loadComponent: () =>
      import('@features/checkout/pages/page-payment-redirect/page-payment-redirect.component').then(
        (m) => m.PagePaymentRedirectComponent,
      ),
    title: 'Betaling',
  },
  {
    // The "Åbn døren" link in a booking mail (#882). No login: the link is the key, as a door code in the
    // mail is. Opening the page opens nothing; its button does.
    path: 'aabn/:token',
    canActivate: [bookingModuleGuard],
    data: { robots: 'noindex' },
    loadComponent: () =>
      import('@features/door-open/page-door-link.component').then((m) => m.PageDoorLinkComponent),
    title: 'Åbn døren',
  },
  {
    // Per-route guards live in checkout.routes.ts — payment-complete and error must stay public
    // for Pay4It's anonymous return leg, so authGuard can't sit on this parent route (#499).
    // bookingModuleGuard is a different matter: on a tenant that has booking it returns true
    // and changes nothing, and a tenant without booking never sends anyone to Pay4It, so there
    // is no anonymous return leg to keep open (#586).
    path: 'checkout',
    canActivate: [bookingModuleGuard],
    data: { robots: 'noindex' },
    loadChildren: () => import('@features/checkout/checkout.routes').then((m) => m.checkoutRoutes),
  },
  {
    // Not guarded as a whole: `unauthorized` must stay reachable so a background 401 shows the
    // page instead of silently bouncing home. Only `signin` is gated — see authentication.routes.ts.
    path: 'authentication',
    data: { robots: 'noindex' },
    loadChildren: () =>
      import('@core/authentication/authentication.routes').then((m) => m.authenticationRoutes),
  },
  {
    path: 'contact',
    loadChildren: () => import('@features/contact/contact.routes').then((m) => m.contactRoutes),
  },
  // Design-system pages: dev and staging builds only (#906).
  ...DEV_ROUTES,
  {
    path: 'practical',
    loadChildren: () =>
      import('@features/practical/practical.routes').then((m) => m.practicalRoutes),
  },
  {
    path: 'basket',
    canActivate: [bookingModuleGuard],
    data: { robots: 'noindex' },
    loadChildren: () => import('@features/basket/basket.routes').then((m) => m.basketRoutes),
  },
  {
    // /search is timeslot search, so it goes with the booking module rather than surviving as
    // content search — decided in #587. If content search is ever built it gets its own route.
    path: 'search',
    canActivate: [bookingModuleGuard],
    loadChildren: () => import('@features/search/search.routes').then((m) => m.searchRoutes),
  },
  {
    path: 'activities',
    loadChildren: () =>
      import('@features/activities/activities.routes').then((m) => m.activitiesRoutes),
  },
  {
    path: 'locations',
    loadChildren: () =>
      import('@features/locations/locations.routes').then((m) => m.locationsRoutes),
  },
  {
    path: 'resources',
    loadChildren: () =>
      import('@features/resources/resources.routes').then((m) => m.resourcesRoutes),
  },
  {
    path: 'legal',
    loadChildren: () => import('@features/legal/legal.routes').then((m) => m.legalRoutes),
  },
  {
    path: 'foreninger',
    loadChildren: () =>
      import('@features/associations/associations.routes').then((m) => m.associationsRoutes),
  },
  {
    // On the parent, not next to authGuard on the child shell in account.routes.ts: resolved
    // first, so a blocked tenant lands on `/` rather than at sign-in, and never loads the
    // page component. (It does still fetch the routes chunk — canActivate runs after route
    // recognition, which is what resolves loadChildren; only canMatch runs before it.)
    // Do not hoist these three onto a shared pathless parent — `path: ''` also matches this
    // guard's own `/` redirect target, which loops forever (see app.routes.spec.ts).
    path: 'account',
    canActivate: [accountModuleGuard],
    data: { robots: 'noindex' },
    loadChildren: () => import('@features/account/account.routes').then((m) => m.accountRoutes),
  },
  {
    path: 'subscriptions',
    canActivate: [subscriptionModuleGuard],
    loadChildren: () =>
      import('@features/subscriptions/subscriptions.routes').then((m) => m.subscriptionsRoutes),
  },
  {
    path: '',
    loadChildren: () => import('@features/home/home.routes').then((m) => m.homeRoutes),
  },
  // Last: a URL nothing above matches (#905).
  notFoundRoute,
];

export const routes: Routes = [
  {
    // Front-channel logout from abook-auth: framed in a zero-size iframe, never navigated to by
    // a person. Outside the maintenance-mode wrapper so the logout still lands while the site
    // is closed — the guard would otherwise redirect the iframe to the maintenance page.
    path: 'signout-frontchannel',
    loadComponent: () =>
      import(
        '@core/authentication/pages/page-signout-frontchannel/page-signout-frontchannel.component'
      ).then((m) => m.PageSignoutFrontchannelComponent),
  },
  {
    path: MAINTENANCE_MODE_PATH,
    loadComponent: () =>
      import(
        '@features/maintenance-mode/pages/page-maintenance-mode/page-maintenance-mode.component'
      ).then((m) => m.PageMaintenanceModeComponent),
    title: 'Midlertidigt lukket',
    // The guard sends every URL here with status 200 while the site is closed.
    data: { robots: 'noindex' },
  },
  {
    path: '',
    canActivateChild: [maintenanceModeGuard],
    children: appRoutes,
  },
];
